<template>
  <v-form id="intervention_form" class="intervention-form" v-model="valid" ref="form" lazy-validation>
    <v-container fluid>
      <v-layout row wrap>
        <v-flex xs12>
          <v-select attach
                    name="studentId"
                    id="createIntervention-student"
                    content-class="createIntervention-content-student"
                    :label="`${ $t('student.student') }`"
                    autocomplete
                    :filter="x => x"
                    :loading="loading"
                    :disabled="!!interventionId"
                    required
                    :items="students"
                    :rules="requiredRule"
                    :search-input.sync="searchStudents"
                    @change="studentChange"
                    v-model="selectedStudent"
                    v-if="interventionMode === 'INDIVIDUAL'" />
          <div v-if="interventionMode === 'GROUP'">
            <p>{{ $t('you_select') }}
              <strong>{{selectedGroup.name}}</strong>
              ({{ $t('watchlist_results', {count: selectedGroup.results}, selectedGroup.results) }})
            </p>
          </div>
        </v-flex>

        <v-flex xs12>
          <v-select attach
                    name="categoryId"
                    id="createIntervention-category"
                    content-class="createIntervention-content-category"
                    :items="interventionFields"
                    v-model="interventionCategorySelected"
                    :rules="requiredRule"
                    required
                    :label="$t('intervention_category')" />
        </v-flex>

        <v-flex xs12>
          <h6 class="subheading">{{ $t('intervention_description_text') }}</h6>
          <v-text-field id="createIntervention-description"
                        name="description"
                        multi-line
                        :label="$t('intervention_description_label')"
                        required
                        :rules="requiredTextRule"
                        v-model="description" />
        </v-flex>

        <v-flex xs12>
          <span class="subheading">{{ $t('intervention_communication_text') }}</span>
          <v-select attach
                    name="communicationApproachId"
                    id="createIntervention-communication"
                    content-class="createIntervention-content-communication"
                    :items="communicationFields"
                    v-model="selectedCommunication"
                    :label="$t('communication')"
                    required
                    :rules="requiredRule" />
        </v-flex>

        <v-flex xs12>
          <h6 class="subheading">{{ $t('intervention_communication_date') }}*</h6>
          <v-menu id="createIntervention-communication-date"
                  lazy
                  :close-on-content-click="false"
                  v-model="communicationPickerMenu"
                  transition="scale-transition"
                  offset-y
                  nudge-left="8"
                  full-width
                  attach
                  max-width="290px"
                  min-width="290px">
            <v-text-field slot="activator"
                          v-model="communicationDateFormatted"
                          prepend-icon="event"
                          @blur="communicationDate = parseDate(communicationDateFormatted)"
                          readonly />
            <v-date-picker id="createIntervention-communication-date-picker"
                           v-model="communicationDate"
                           :locale="currentLocale"
                           @input="communicationDateSelected"
                           scrollable
                           :allowed-dates="allowDatesTodayOrBefore" />
          </v-menu>
        </v-flex>

        <v-flex xs12>
          <h6 class="subheading">{{ $t('intervention_action_text') }}</h6>
          <v-text-field name="studentAction"
                        id="createIntervention-action"
                        :label="$t('action')"
                        multi-line
                        v-model="action" />
        </v-flex>

        <v-flex xs12>
          <h6 class="subheading">{{ $t('intervention_duration_text') }}</h6>
          <vue-slider :min="meetingSliderMin" :max="meetingSliderMax" :value="meetingDuration" :interval="10"
                      :show="showSlider" :formatter="formatTooltip"
                      :processStyle="sliderStyle" :labelStyle="{color: this.$vuetify.theme.primary}" :tooltipStyle="tooltipStyle" tooltip="hover" v-on:input="sliderInput"
                      :piecewise="false" :dot-size="22" :piecewiseLabel="true" />
          <v-flex pt-3>
            <v-text-field :label="$t('intervention_form_duration_amount', meetingDuration, meetingDuration)"
                          :min="meetingSliderMin"
                          :max="meetingSliderMax"
                          mask="##"
                          type="tel"
                          step="1"
                          v-model="meetingDuration" />
          </v-flex>
        </v-flex>

        <v-flex xs12>
          <h6 class="subheading">{{ $t('intervention_date_text') }}</h6>
          <v-menu id="createIntervention-date"
                  lazy
                  attach
                  :close-on-content-click="false"
                  v-model="pickerMenu"
                  transition="scale-transition"
                  offset-y
                  nudge-left="8"
                  full-width
                  max-width="290px"
                  min-width="290px">
            <v-text-field slot="activator"
                          :label="$t('intervention_date_label')"
                          v-model="dateFormatted"
                          prepend-icon="event"
                          @blur="date = parseDate(dateFormatted)"
                          readonly />
            <v-date-picker id="createIntervention-date-picker"
                           v-model="date"
                           :locale="currentLocale"
                           @input="dateSelected"
                           scrollable
                           :allowed-dates="allowedDates" />
          </v-menu>
        </v-flex>

        <v-flex xs12 v-if="interventionMode === 'INDIVIDUAL' && !groupId">
          <v-flex xs12>
            <v-layout row wrap>
              <v-flex pt-2>
                <h6 class="subheading">{{ $t('intervention_reminder_text') }}</h6>
              </v-flex>
              <v-flex>
                <v-select attach
                          id="createIntervention-reminder"
                          class="pt-0"
                          content-class="createIntervention-content-reminder"
                          name="reminderEmailFrequency"
                          :items="dayFields"
                          v-model="reminderInDays" />
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12>
            <v-checkbox name="repeatingEmail" v-if="reminderInDays > 0"
                        class="createIntervention-repeating"
                        v-model="repeatingEmail"
                        :label="$t('intervention_referral_repeating_email')"
                        color="primary" />
          </v-flex>

          <v-flex xs12 v-if="repeatingEmail" class="endDateContainer">
            <v-layout row>
              <v-flex>
                <h6 class="subheading center">{{ $t('intervention_repeating_email_end_date_text') }}</h6>
              </v-flex>
              <v-flex>
                <v-menu id="createIntervention-end-date"  class="endDateMenu"
                  lazy
                  :close-on-content-click="false"
                  v-model="repeatingEmailEndDatePickerMenu"
                  transition="scale-transition"
                  offset-y
                  nudge-left="8"
                  full-width
                  attach
                  max-width="290px"
                  min-width="290px">
                <v-text-field
                    slot="activator"
                    v-model="repeatingEmailEndDateFormatted"
                    :label="$t('intervention_repeating_email_end_date_label')"
                    @blur="repeatingEmailEndDate = parseDate(repeatingEmailEndDateFormatted)"
                    prepend-icon="event"
                    ref="repeatingEndDate"
                    :rules="repeatingEndDateRule"
                    readonly />
                  <v-date-picker id="createIntervention-communication-date-picker"
                          v-model="repeatingEmailEndDate"
                          :locale="currentLocale"
                          @input="repeatingEmailEndDateSelected"
                          scrollable
                          :allowed-dates="allowDatesTodayOrAfter" />

                </v-menu>
              </v-flex>
            </v-layout>
            <v-layout>
              <!-- <v-flex v-if="showRepeatingEndDateWarning">
                <span :style="{ color: this.$vuetify.theme.accent}">
                  {{ $t('repeating_email_invalid_end_date_error_message') }}
                </span>
              </v-flex> -->
            </v-layout>
          </v-flex>


          <v-flex v-if="interventionId">
            <h6 class="subheading">{{ $t('intervention_referral_text') }}</h6>
            <v-subheader class="signposts__label mt-1 mb-1">{{ $t('email_referral_title') }}</v-subheader>
            <p v-if="referralInfoList.length === 0">
              {{ $t('no_email_referral') }}
            </p>
            <div v-for="(referral, index) in referralInfoList" :key="index" class="mb-3">
              <p class="referral-email mb-1">{{ $t('intervention_referral_email') }}
                <span>{{ referral.referralDescription }}</span>
              </p>
              <p class="referral-date">{{ referralDate | time }}</p>
            </div>
          </v-flex>

          <v-flex v-if="!interventionId">
            <h6 class="subheading">{{ $t('intervention_referral_text') }}</h6>
            <v-flex xs12 mb-3 v-for="(referral, index) in referralList" :key="index">
              <InteractionReferral :item="referral"
                                   :itemId="index"
                                   :class="{'bordered': index !== 0 }"
                                   :style="{borderColor: index !== 0 ? $vuetify.theme.primary : 'transparent'}"
                                   @getReferralValue="getReferralValue"
                                   @getReferralSummary="getReferralSummary"
                                   @getRecipients="getRecipients" />
            </v-flex>
            <v-flex xs12 mt-4 mb-4 v-if="!hasNoReferrals">
              <v-btn outline
                     color="primary"
                     class="add-button w-100 ma-0"
                     @click="addReferral">
                <v-icon size="18px" class="mr-2">stream-add</v-icon>
                <span>{{ $t('referral_add') }}</span>
              </v-btn>
            </v-flex>
          </v-flex>

          <v-flex xs12 pb-4 v-if="signpostData" :class="{'blur-backdrop': interventionId }">
            <v-subheader class="signposts__label">{{ $t('signpost_referral_title') }}</v-subheader>
            <v-checkbox v-for="(signpost, index) in signpostData"
                        :key="index"
                        v-model="selected"
                        :value="signpost.value"
                        class="signposts__checkbox multiline-checkbox"
                        :disabled="!!interventionId"
            >
              <div slot="label">
                <span class="subheading">{{ $t('signpost_item') }}</span>
                <a target="_blank" :href="signpost.url" class="prevent-overflow">
                  {{ $t(signpost.urlKey) || $t(signpost.text) }}
                </a>
              </div>
            </v-checkbox>
          </v-flex>
        </v-flex>

        <v-flex xs12>
          <v-checkbox slot="activator" name="consent"
                      class="createIntervention-consent multiline-checkbox"
                      v-model="consent"
                      :label="$t('intervention_consent')"
                      :disabled="!!interventionId"
                      v-if="flagReferralConsent && (hasReferrals || selected.length)"
                      color="primary"
                      hide-details />

          <v-checkbox slot="activator" name="closeOnCreation"
                      class="createIntervention-close multiline-checkbox"
                      v-model="closeOnCreation"
                      :label="$t('intervention_close_on_creation')"
                      v-show="!interventionId"
                      color="primary"
                      hide-details />
        </v-flex>

        <v-flex xs12 my-3 class="caption">
          *{{ $t('required_fields') }}
        </v-flex>
      </v-layout>

      <v-layout row justify-center>
        <v-btn id="createIntervention-cancel"
               @click="cancel"
               :disabled="sending">
          {{ $t('cancel') }}
        </v-btn>
        <transition name="wiggle">
          <v-btn id="createIntervention-create"
                 @click="createIntervention"
                 color="primary"
                 :disabled="flagReferralConsent ? (((hasReferrals || selected.length) && !consent) || sending) : sending"
                 :loading="sending"
                 v-if="sendingShow">
            <span v-if="!interventionId">{{ $t('create') }}</span>
            <span v-if="interventionId">{{ $t('update') }}</span>
          </v-btn>
        </transition>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
  import moment from 'moment-es6'
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
  import {
    SET_INTERVENTION_ACTION,
    SET_INTERVENTION_CATEGORY,
    SET_INTERVENTION_CLOSE_ON_CREATE,
    SET_INTERVENTION_CONSENT,
    SET_INTERVENTION_COMMUNICATION,
    SET_INTERVENTION_COMMUNICATION_DATE,
    SET_INTERVENTION_COMMUNICATION_DATE_FORMATTED,
    SET_INTERVENTION_REPEATING_EMAIL_END_DATE,
    SET_INTERVENTION_REPEATING_EMAIL_END_DATE_FORMATTED,
    SET_INTERVENTION_DATE,
    SET_INTERVENTION_DATE_FORMATTED,
    SET_INTERVENTION_DESCRIPTION,
    SET_INTERVENTION_DURATION,
    SET_INTERVENTION_FORM_VALID,
    SET_INTERVENTION_REMINDER_DAYS,
    SET_INTERVENTION_REPEATING_EMAIL,
    SET_INTERVENTION_STUDENT,
    SET_INTERVENTION_STUDENTS,
    DECREMENT_SSO_REFRESH_FREQUENCY,
    RESET_SSO_REFRESH_ATTEMPTS,
    ADD_INTERVENTION_REFERRAL,
    SET_INTERVENTION_REFERRAL_ID,
    SET_INTERVENTION_REFERRAL_SUMMARY,
    SET_INTERVENTION_REFERRAL_RECIPIENTS
  } from '../../store/mutation-types'
  import {
    GET_INTERVENTION_STUDENTS_LIST,
    POST_BULK_INTERVENTION_FORM,
    POST_INTERVENTION_FORM,
    REFRESH_SSO_TOKEN
  } from '../../store/action-types'
  import { getRange } from '../../HelperMethods'
  import { HAS_FEATURE_FLAG, HAS_NO_REFERRALS, HAS_REFERRALS } from '../../store/getter-types'
  import Helpers from '../../mixins/Helpers'
  import VueScrollTo from 'vue-scrollto'
  import vueSlider from 'vue-slider-component'
  import TooltipStyle from '../../mixins/TooltipStyle'
  import InteractionReferral from './InteractionReferral'
  import DateFormat from '../../mixins/DateFormat'

  export default {
    name: 'intervention-form',
    props: ['cancel'],
    mixins: [Helpers, TooltipStyle, DateFormat],
    components: { InteractionReferral, vueSlider },
    data () {
      // Delay, else may fail to work.
      setTimeout(() => {
        this.showSlider = true
      }, 500)

      return {
        selected: [],
        signpostList: [],
        snackbarTimeout: 2500,
        snackBarShow: true,
        searchStudents: null,
        days: [...getRange(1, 7), 14, 28],
        pickerMenu: false,
        repeatingEmailEndDatePickerMenu: false,
        communicationPickerMenu: false,
        sendingShow: true,
        showSlider: false,
        meetingSliderMax: 60,
        meetingSliderMin: 0,
        requiredRule: [
          v => !!v || this.$t('field_required')
        ],
        repeatingEndDateRule: [
          v => !!v || this.$t('field_required'),
          value => {
            if (!this.reminderInDays || !this.repeatingEmail || !value) {
              return true
            }
            const repeatingEmailEndDate = moment(this.parseDate(value)).startOf('day')
            const minimumDate = moment().startOf('day').add(this.reminderInDays, 'days')
            if (repeatingEmailEndDate.diff(minimumDate, 'days') > 0) {
              return true
            }
            return this.$t('repeating_email_invalid_end_date_error_message')
          }],
        requiredTextRule: [
          v => !!v.trim() || this.$t('field_required')
        ]
      }
    },
    watch: {
      searchStudents (val) {
        val && this.queryStudents(val)
      },
      rightDrawerState () {
        if (this.$store.state.rightDrawer.state) {
          this.checkSSOStatus()
        } else {
          this.killRefresh()
        }
      },
      selected (value) {
        const list = []
        value.forEach((v) => {
          if (list.indexOf(v) === -1) {
            list.push({
              refereeId: v,
              referralSummary: '',
              recipients: []
            })
          }
        })
        this.signpostList = list
      },
      selectedSignposts (value) {
        this.selected = value
      }
    },
    methods: {
      ...mapActions({
        queryStudents: GET_INTERVENTION_STUDENTS_LIST
      }),
      ...mapMutations({
        addInterventionReferral: ADD_INTERVENTION_REFERRAL,
        setInterventionReferralId: SET_INTERVENTION_REFERRAL_ID,
        setInterventionReferralSummary: SET_INTERVENTION_REFERRAL_SUMMARY,
        setInterventionReferralRecipients: SET_INTERVENTION_REFERRAL_RECIPIENTS
      }),
      formatTooltip (val) {
        return val === this.meetingSliderMax ? `${val}+` : val
      },
      createIntervention () {
        if (this.$refs.form.validate()) {
          let payload = {}

          // Gets most of the grunt work done. For simple fields just add name='server_key' to the form element.
          this.$refs.form.getInputs()
            .filter(field => field.$attrs.name !== undefined)
            .forEach((inputField) => {
              payload[inputField.$attrs.name] = inputField.value || inputField.inputValue
            })

          payload['actionTargetDate'] = this.date
          payload['meetingDuration'] = this.meetingDuration
          payload['dateOfCommunication'] = this.communicationDate
          payload['consented'] = this.consent
          payload['sendEmailToStudent'] = this.flagEmailSendCheckBox || false
          if (!this.interventionId) {
            const referralList = this.referralList.filter(referral => referral.refereeId !== null)
            payload['referralList'] = referralList.concat(this.signpostList)
          }
          if (this.repeatingEmail) {
            payload['repeatingEmailEndDate'] = this.repeatingEmailEndDate || null
          }
          if (this.interventionMode === 'GROUP') {
            payload.myGroupId = this.selectedGroup.id
            this.$store.dispatch(POST_BULK_INTERVENTION_FORM, payload)
          } else {
            this.$store.dispatch(POST_INTERVENTION_FORM, payload)
          }
          this.killRefresh()
        } else {
          // Makes the button wiggle around due to the animation associated with it
          this.sendingShow = false
          this.$nextTick(() => {
            this.sendingShow = true
          })
          // Scroll to the first element with a validation error
          for (let i = 0; i < this.$refs.form.getInputs().length; i++) {
            if (this.$refs.form.getInputs()[i].hasError) {
              let options = {
                container: '#right-drawer',
                easing: 'ease-in-out',
                offset: -64,
                cancelable: false
              }
              VueScrollTo.scrollTo(this.$refs.form.getInputs()[i].$el, 500, options)
              break
            }
          }
        }
      },
      allowedDates (date) {
        // Allows today and in the future
        const now = moment()
        const then = moment(date).startOf('day')
        return then.diff(now, 'days') >= 0
      },
      allowDatesTodayOrBefore (date) {
        // Allows today and in the past
        const now = moment().startOf('day')
        const then = moment(date)
        return then.diff(now, 'days') <= 0
      },
      allowDatesTodayOrAfter (date) {
        const now = moment().startOf('day')
        const then = moment(date)
        return then.diff(now, 'days') > 0
      },
      formatDate (date) {
        if (!date) {
          return null
        }
        return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
      },
      parseDate (date) {
        if (!date) {
          return null
        }
        return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
      },
      dateSelected (value) {
        this.dateFormatted = this.formatDate(value)
        this.pickerMenu = false
      },
      communicationDateSelected (value) {
        this.communicationDateFormatted = this.formatDate(value)
        this.communicationPickerMenu = false
      },
      repeatingEmailEndDateSelected (value) {
        this.repeatingEmailEndDateFormatted = this.formatDate(value)
        this.repeatingEmailEndDatePickerMenu = false
      },
      studentChange (data) {
        /* data is different types
             number - a valid student
             event - an unknown student name.
           The issue was unknown names appeared blank without a validation error.
         */
        if (data.target && data.target.value === '' && this.selectedStudent !== '') {
          this.selectedStudent = ''
        }
      },
      sliderInput (value) {
        this.meetingDuration = Number(value)
      },
      refreshSSOToken () {
        this.$store.dispatch(REFRESH_SSO_TOKEN)
        this.$store.commit(DECREMENT_SSO_REFRESH_FREQUENCY)
        if (this.ssoRefreshAttempts < 1) {
          clearInterval(this.interval1)
        }
      },
      checkSSOStatus () {
        if (this.ssoRefreshAttempts > 0 && this.ssoRefreshFrequency > 0) {
          this.interval1 = setInterval(
            function () {
              this.refreshSSOToken()
            }.bind(this),
            this.ssoRefreshFrequency
          )
        }
      },
      killRefresh () {
        clearInterval(this.interval1)
        this.$store.commit(RESET_SSO_REFRESH_ATTEMPTS)
      },
      addReferral () {
        this.addInterventionReferral()
      },
      getReferralValue (val) {
        this.setInterventionReferralId({index: val.id, value: val.value})
      },
      getReferralSummary (val) {
        this.setInterventionReferralSummary({index: val.id, text: val.text})
      },
      getRecipients (val) {
        this.setInterventionReferralRecipients({index: val.id, recipients: val.recipients})
      }
    },
    computed: {
      ...mapState({
        groupId: state => state.intervention.groupId,
        referralList: state => state.intervention.referralList,
        referralInfoList: state => state.intervention.referralInfoList,
        referralDate: state => state.intervention.referralDate,
        selectedSignposts: state => state.intervention.signpostList,
        communications: state => state.intervention.communications,
        categories: state => state.intervention.categories,
        referrals: state => state.intervention.referrals,
        loading: state => state.intervention.loading,
        sending: state => state.intervention.sending,
        interventionId: state => state.intervention.interventionId,
        currentLocale: state => state.i18n.locale,
        moduleInterExem: state => state.profile.module.interexem,
        functionInterventionForm: state => state.intervention.functions.interventionForm,
        optionSingleInterventionCategory: state => state.intervention.options.enable.singleInterventionCategory,
        optionReferralConsent: state => state.intervention.options.enable.referralConsent,
        optionSendEmailCheckbox: state => state.intervention.options.enable.emailSend,
        ssoRefreshFrequency: state => state.config.ssoRefreshFrequency,
        ssoRefreshAttempts: state => state.config.ssoRefreshAttempts,
        rightDrawerState: state => state.rightDrawer.state,
        interventionMode: state => state.intervention.interventionMode,
        selectedGroup: state => state.groups.selectedGroup
      }),
      ...mapGetters({
        hasFeatureFlag: HAS_FEATURE_FLAG,
        hasReferrals: HAS_REFERRALS,
        hasNoReferrals: HAS_NO_REFERRALS
      }),
      dayFields () {
        let dayFields = []
        dayFields.push({
          'text': this.$t('email_no_reminder'),
          'value': 0
        })
        this.days.forEach(day => {
          const fieldText = this.$t('days', {day: day}, day)
          dayFields.push({
            'text': fieldText,
            'value': day
          })
        })
        return dayFields
      },
      interventionFields () {
        return this.translateFields(this.categories)
      },
      communicationFields () {
        return this.translateFields(this.communications)
      },
      referralFields () {
        return this.translateFields(this.referrals)
      },
      interventionCategorySelected: {
        get () {
          if (this.flagSingleInterventionCategory && this.interventionFields.length === 1) {
            return this.interventionFields[0].value
          } else {
            return this.$store.state.intervention.interventionCategorySelected
          }
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_CATEGORY, value)
        }
      },
      selectedStudent: {
        get () {
          return this.$store.state.intervention.selectedStudent
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_STUDENT, value)
        }
      },
      selectedCommunication: {
        get () {
          if (this.flagSingleInterventionCategory && this.communicationFields.length === 1) {
            return this.communicationFields[0].value
          } else {
            return this.$store.state.intervention.selectedCommunication
          }
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_COMMUNICATION, value)
        }
      },
      reminderInDays: {
        get () {
          return this.$store.state.intervention.reminderInDays
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REMINDER_DAYS, value)
          this.$refs.repeatingEndDate.validate()
        }
      },
      repeatingEmail: {
        get () {
          return this.$store.state.intervention.repeatingEmail
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REPEATING_EMAIL, value)
        }
      },
      date: {
        get () {
          return this.$store.state.intervention.date
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DATE, value)
          if (!this.repeatingEmailEndDate) {
            return
          }
          const selectedDate = moment(value)
          const isEndBefore = moment(this.repeatingEmailEndDate).diff(selectedDate, 'days') <= 0
          if (isEndBefore) {
            this.repeatingEmailEndDate = null
            this.repeatingEmailEndDateFormatted = null
          }
        }
      },
      dateFormatted: {
        get () {
          return this.$store.state.intervention.dateFormatted
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DATE_FORMATTED, value)
        }
      },
      communicationDate: {
        get () {
          return this.$store.state.intervention.dateOfCommunication
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_COMMUNICATION_DATE, value)
        }
      },
      communicationDateFormatted: {
        get () {
          return this.$store.state.intervention.dateOfCommunicationFormatted
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_COMMUNICATION_DATE_FORMATTED, value)
        }
      },
      repeatingEmailEndDate: {
        get () {
          return this.$store.state.intervention.repeatingEmailEndDate
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REPEATING_EMAIL_END_DATE, value)
        }
      },
      repeatingEmailEndDateFormatted: {
        get () {
          return this.$store.state.intervention.repeatingEmailEndDateFormatted
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REPEATING_EMAIL_END_DATE_FORMATTED, value)
        }
      },
      action: {
        get () {
          return this.$store.state.intervention.action
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_ACTION, value)
        }
      },
      students: {
        get () {
          return this.$store.state.intervention.students
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_STUDENTS, value)
        }
      },
      valid: {
        get () {
          return this.$store.state.intervention.valid
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_FORM_VALID, value)
        }
      },
      meetingDuration: {
        get () {
          return this.$store.state.intervention.meetingDuration
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DURATION, value)
        }
      },
      description: {
        get () {
          return this.$store.state.intervention.description
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DESCRIPTION, value)
        }
      },
      closeOnCreation: {
        get () {
          return this.$store.state.intervention.closeOnCreation
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_CLOSE_ON_CREATE, value)
        }
      },
      consent: {
        get () {
          return this.$store.state.intervention.consent
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_CONSENT, value)
        }
      },
      signpostData () {
        return this.referrals.filter(r => r.url)
      },
      flagSingleInterventionCategory () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionInterventionForm + '.' + this.optionSingleInterventionCategory)
      },
      flagReferralConsent () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionInterventionForm + '.' + this.optionReferralConsent)
      },
      flagEmailSendCheckBox () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionInterventionForm + '.' + this.optionSendEmailCheckbox)
      }
    },
    mounted () {
      this.checkSSOStatus()
      this.selected = this.selectedSignposts
    }
  }
</script>

<style scoped>

  /deep/ .createIntervention-content-category .list__tile__title {
    height: auto !important;
    min-height: 24px !important;
    word-wrap: break-word !important;
    white-space: inherit !important;
  }
  .wiggle-enter-active, .wiggle-leave-active {
    animation: wiggle .2s infinite;
  }

  .referral-email span {
    font-weight: 500;
  }

  .referral-date {
    color: rgba(0,0,0,.54);
    margin: 0;
  }

  .signposts__checkbox >>> .input-group__details {
    min-height: auto;
  }

  .signposts__label {
    display: block;
    height: 24px;
    /* Normally font size gets scaled by 0.75 when label goes over a select item. Match the 'subheader' font size. */
    font-size: calc(14px * 0.75);
    padding: 0;
    font-weight: initial;
  }

  .createIntervention-consent /deep/ label {
    height: 56px;
    white-space: unset;
  }

  .subheading {
    font-size: 14px !important;
  }

  .subheading.center {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .add-button {
    height: 50px;
    border-style: dashed;
  }

  .blur-backdrop {
    position: relative;
    color: rgba(0,0,0,.38);
  }

  .blur-backdrop a {
    color: rgba(0,0,0,.38);
  }

  .blur-backdrop:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    pointer-events: none;
    user-select: none;
    z-index: 1;
  }

  @keyframes wiggle {
    0% {
      right: 0;
    }
    33% {
      right: 5px;
    }
    66% {
      right: -5px;
    }
    100% {
      right: 0;
    }
  }

  .endDateMenu >>> .menuable__content__active:not(:has(.input-group--error)) {
    left: -100px !important;
  }

  .endDateContainer {
    margin-top: -15px; /* reset the padding-top of datepicker component */
    margin-bottom: 22px; /* height of input-group__details div of vuetify */
    gap: 4px;
  }
  .endDateContainer .flex {
    justify-content: center;
    align-items: center;
  }

  .multiline-checkbox {
    display: flex;
    flex-direction: row-reverse;
  }

  .multiline-checkbox >>> label, .multiline-checkbox {
    display: flex;
    height: unset !important;
    justify-content: flex-start;
    align-items: flex-start;
    position: unset !important;
    text-overflow: unset !important;
    white-space: unset !important;
  }

  .multiline-checkbox >>> .input-group__input {
    flex: 1 !important;
    width: unset !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/intervention/InterventionForm.vue